import { useMemo } from "react";
import type { ConnectorRun } from "../../types";
import type { AggregatedTableRun } from "../RunsTable.utils";
import {
  aggregateTableRuns,
  formatDuration,
  formatTime,
  getConnectorTotals,
  getDurationSeconds,
  getPagesFetched,
} from "../RunsTable.utils";
import { TypeBadge } from "../TypeBadge";
import { ConnectorModeSummary, ModeBadge, StatusBadge } from "./badges";

function FreshnessCell({
  fileName,
  fileTimestamp,
  status,
}: {
  fileName: string | null;
  fileTimestamp: string | null;
  status: "ok" | "stale-file" | null;
}) {
  if (!fileName) return <td></td>;

  const formattedDate = fileTimestamp
    ? new Date(fileTimestamp).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const statusClass = status === "stale-file" ? "freshness-stale" : "freshness-ok";

  return (
    <td className={`cell-freshness ${statusClass}`}>
      <span className="freshness-filename" title={fileName}>
        {fileName.split("/").pop()}
      </span>
      {formattedDate && <span className="freshness-date">{formattedDate}</span>}
      {status === "stale-file" && <span className="freshness-badge">stale</span>}
    </td>
  );
}

function TableRunRow({ tableRun }: { tableRun: AggregatedTableRun }) {
  const duration = getDurationSeconds(tableRun.minStartedAt, tableRun.maxEndedAt);

  return (
    <tr className="detail-row-table">
      <td className="cell-name">
        <span className="indent-table">{tableRun.tableName}</span>
      </td>
      <td></td>
      <td>{formatTime(tableRun.minStartedAt)}</td>
      <td className="cell-numeric">
        {tableRun.minStartPage ?? "–"} → {tableRun.maxEndPage ?? "–"}
      </td>
      <td className="cell-numeric">{formatDuration(duration)}</td>
      <td>
        <StatusBadge status={tableRun.finalStatus} />
      </td>
      <td>
        <ModeBadge mode={tableRun.mode} />
      </td>
      <td className="cell-numeric">{tableRun.runCount}</td>
      <td className="cell-numeric">{tableRun.totalInserted.toLocaleString()}</td>
      <td className="cell-numeric">{tableRun.totalFetched.toLocaleString()}</td>
      <td className="cell-numeric">{tableRun.totalSkipped.toLocaleString()}</td>
      <FreshnessCell
        fileName={tableRun.sourceFileName}
        fileTimestamp={tableRun.sourceFileTimestamp}
        status={tableRun.freshnessStatus}
      />
    </tr>
  );
}

export function ConnectorRunRow({
  connectorRun,
  displayName,
  isExpanded,
  onToggle,
}: {
  connectorRun: ConnectorRun;
  displayName: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const aggregatedTables = useMemo(
    () => aggregateTableRuns(connectorRun.tableRuns),
    [connectorRun.tableRuns],
  );

  const { totalInserted, totalFetched, totalSkipped, tableCount } =
    getConnectorTotals(connectorRun);

  const duration = getDurationSeconds(connectorRun.startedAt, connectorRun.endedAt);
  const totalPages = connectorRun.tableRuns.reduce((sum, tr) => sum + getPagesFetched(tr), 0);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- <tr> stays a table-row; semantic <button> can't replace it without breaking table structure. Whole-row toggle target with keyboard support. */}
      <tr
        className="detail-row-connector"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <td className="cell-name">
          <span className="indent-connector">
            <span className="expand-icon">{isExpanded ? "▼" : "▶"}</span>
            {displayName}
            <TypeBadge type={connectorRun.connector.type} />
          </span>
        </td>
        <td></td>
        <td>{formatTime(connectorRun.startedAt)}</td>
        <td className="cell-numeric">{totalPages > 0 ? totalPages.toLocaleString() : "–"}</td>
        <td className="cell-numeric">{formatDuration(duration)}</td>
        <td>
          <StatusBadge status={connectorRun.status} />
        </td>
        <td>
          <ConnectorModeSummary tableRuns={connectorRun.tableRuns} />
        </td>
        <td className="cell-numeric">{tableCount}</td>
        <td className="cell-numeric">{totalInserted.toLocaleString()}</td>
        <td className="cell-numeric">{totalFetched.toLocaleString()}</td>
        <td className="cell-numeric">{totalSkipped.toLocaleString()}</td>
        <td></td>
      </tr>
      {isExpanded &&
        aggregatedTables.map((table) => <TableRunRow key={table.tableName} tableRun={table} />)}
    </>
  );
}
